import { useDispatch, useSelector } from 'react-redux';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Button, Menu, MenuItem, SvgIcon, Tooltip, useMediaQuery } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { GlobalState } from 'reducers/stateTypes';
import { User } from 'types/User';
import { CLM_TO_CRM_URL, SMALL_SCREEN_WIDTH } from 'services/constants';
import { useFetchLegalPartner, useFetchPrivateIndividualPartner } from 'hooks/queries/useFetchPartner';
import { BrStatus, PartnerStatus, PartnerType } from 'types/Partner';
import { openModal } from 'reducers/modalReducer';
import { useFetchBusinessRelations } from 'hooks/queries/useFetchClmBrs';
import { hasBRWithInActiveStatus, hasBRWithIntermediaryB2B } from 'utils/businessRelations';
import { useFetchPartnerVersions } from 'hooks/queries/useFetchPartnerVersions';
import { usePartnerDlmV2Enabled } from 'hooks/queries/usePartnerDlmV2Enabled';
import { JBIcon } from '@bjb-gcrm/gcrm-frontend-components';
import { assertNotNullOrUndefined } from 'utils/utils';

const RejectWithdrawButton = ({
  partnerType,
  isPartnerLockEnabled,
}: {
  partnerType: PartnerType;
  isPartnerLockEnabled?: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const user: User = useSelector((state: GlobalState) => state.user?.userDetails);
  const dispatch = useDispatch();
  const { id: techId } = useParams<{ id: string }>();
  assertNotNullOrUndefined(techId);
  const partnerCall =
    partnerType === PartnerType.INDIVIDUAL_PARTNER ? useFetchPrivateIndividualPartner : useFetchLegalPartner;
  const { data: partner } = partnerCall(techId);
  const { t } = useTranslation();

  const smallScreen = useMediaQuery(`(max-width:${SMALL_SCREEN_WIDTH})`);

  const { data: brList = [], isFetching: isBRFetchingInProgress } = useFetchBusinessRelations(techId, false, true);

  const hasAnyBRWithIntermediaryB2B = hasBRWithIntermediaryB2B(brList);

  const isPartnerDlmV2 = usePartnerDlmV2Enabled();
  const {
    data: partnerVersions,
    refetch: refetchVersions,
    isFetching: isVersionsFetching,
    isError: isVersionsError,
  } = useFetchPartnerVersions(techId);
  const hasDraftVersion = partnerVersions?.some?.(({ partnerStatus }) => partnerStatus === 'DRAFT') ?? false;

  // DLM v2: versions are reloaded on every "More" click. Fail closed: keep the items disabled while the reload
  // runs or after it failed, since the backend does not block a status change while a DLM draft exists.
  const isDLMPartnerAndHasDraftVersion =
    isPartnerLockEnabled && (hasDraftVersion || (isPartnerDlmV2 && (isVersionsFetching || isVersionsError)));

  const openMenu = (target: HTMLElement) => {
    setAnchorEl(target);
    if (isPartnerDlmV2) {
      refetchVersions();
    }
  };

  const hasAnyInActiveBRWithLifecycleAsClosed = hasBRWithInActiveStatus(brList);

  const isIntermediaryB2BDisabled = isBRFetchingInProgress || hasAnyBRWithIntermediaryB2B;

  /**
   * The tooltip that is displayed when hovered over in the dropdown menu.
   */
  let tooltipFeedbackReject = '';
  let tooltipFeedbackWithdraw = '';
  let tooltipFeedbackReassign = '';
  let tooltipFeedbackProspectReActivate = '';

  const checkUserEligibilityProspectReActivate = () => {
    if (
      user?.hasProspectReactivateAuthority &&
      (user?.hasFrontTeamHeadAccessAuthority ||
        partner?.relationshipManager === user?.username ||
        user?.hasRMAAccessAuthority)
    ) {
      return true;
    }
    tooltipFeedbackProspectReActivate = `${tooltipFeedbackProspectReActivate} ${t('more.reactivate.prospect.tooltip.message')}`;
    return false;
  };

  const checkUserEligibilityWithdraw = () => {
    if (user?.hasWithdrawProspectAuthority && (user?.hasFrontTeamHeadAccessAuthority || user?.hasRMAAccessAuthority)) {
      return true;
    }
    if (!user?.hasWithdrawProspectAuthority || partner?.relationshipManager !== user?.username) {
      tooltipFeedbackWithdraw = `${tooltipFeedbackWithdraw} ${t('more.withdraw.partners.tooltip.message')}`;
      return false;
    } else {
      return true;
    }
  };

  const checkUserEligibilityReject = () => {
    if (user?.hasRejectProspectAuthority && (user?.hasFrontTeamHeadAccessAuthority || user?.hasRMAAccessAuthority)) {
      return true;
    }
    if (!user?.hasRejectProspectAuthority || partner?.relationshipManager !== user?.username) {
      tooltipFeedbackReject = `${tooltipFeedbackReject} ${t('more.reject.partners.tooltip.message')}`;
      return false;
    } else {
      return true;
    }
  };

  const STATUS_TO_ENABLE = [BrStatus.DRAFT, BrStatus.IN_APPROVAL, BrStatus.READY_FOR_LINKING];

  const isStatusDisabled: boolean | null = partner && ![...STATUS_TO_ENABLE].includes(partner?.brStatus);
  const isReactivateStatusDisabled: boolean | null =
    partner && ![BrStatus.WITHDRAWN, BrStatus.REJECTED].includes(partner?.brStatus);

  const isStatusDisabledForReassign =
    partner &&
    ![...STATUS_TO_ENABLE, BrStatus.DUPLICATE_CHECK_PENDING, BrStatus.ONBOARDING].includes(
      partner?.brStatus as BrStatus,
    );

  const isWithdrawDisabled = !checkUserEligibilityWithdraw();
  const isProspectReActivateDisabled = !checkUserEligibilityProspectReActivate();
  const isRejectDisabled = !checkUserEligibilityReject();
  const crmLink = `${CLM_TO_CRM_URL}${partner?.globalPartnerId}`;

  if (isStatusDisabled) {
    tooltipFeedbackReject = `${tooltipFeedbackReject} ${t('more.reject.tooltip.status')}`;
    tooltipFeedbackWithdraw = `${tooltipFeedbackWithdraw} ${t('more.withdrawn.tooltip.status')}`;
  }

  if (isStatusDisabledForReassign) {
    tooltipFeedbackReassign = t('more.reassign.tooltip.status');
  }
  if (isReactivateStatusDisabled) {
    tooltipFeedbackProspectReActivate = `${tooltipFeedbackProspectReActivate} ${t('more.reactivateStatus.tooltip')}`;
  }

  if (isDLMPartnerAndHasDraftVersion) {
    tooltipFeedbackReject = `${tooltipFeedbackReject} ${t('more.reject.button.dlmAccess')}`;
    tooltipFeedbackWithdraw = `${tooltipFeedbackWithdraw} ${t('more.withdraw.button.dlmAccess')}`;
    tooltipFeedbackReassign = `${tooltipFeedbackReassign} ${t('more.reassign.button.dlmAccess')}`;
  }

  return (
    <>
      {smallScreen ? (
        <SvgIcon
          onClick={(event: any) => {
            openMenu(event.currentTarget);
          }}
          color="primary"
        >
          <MoreVertIcon />
        </SvgIcon>
      ) : (
        <Button
          sx={{ maxHeight: '32px' }}
          id="withdraw-reject-button"
          aria-haspopup="true"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            openMenu(event.currentTarget);
          }}
          startIcon={<JBIcon iconName="additionalFunctions" />}
          variant="outlined"
        >
          More
        </Button>
      )}

      <Menu
        id="withdraw-reject-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Tooltip title={tooltipFeedbackReject} arrow placement="left-start" id="reject-tooltip">
          <span id="reject-span">
            <MenuItem
              disabled={
                isStatusDisabled || isRejectDisabled || isIntermediaryB2BDisabled || isDLMPartnerAndHasDraftVersion
              }
              id="menuitem-reject"
              onClick={async () => {
                dispatch(
                  openModal({
                    modalType: 'RejectWithdrawDialog',
                    modalProps: {
                      modalType: PartnerStatus.REJECTED,
                      partner: partner,
                    },
                  }),
                );
                setAnchorEl(null);
              }}
            >
              <>
                {t('more.reject.title')}
                {(isStatusDisabled || isRejectDisabled) && <InfoOutlinedIcon style={{ maxHeight: '15px' }} />}
              </>
            </MenuItem>
          </span>
        </Tooltip>

        <Tooltip title={tooltipFeedbackWithdraw} placement="left-start" arrow>
          <span id="withdraw-span">
            <MenuItem
              disabled={
                isStatusDisabled || isWithdrawDisabled || isIntermediaryB2BDisabled || isDLMPartnerAndHasDraftVersion
              }
              id="menuitem-withdraw"
              onClick={() => {
                dispatch(
                  openModal({
                    modalType: 'RejectWithdrawDialog',
                    modalProps: {
                      modalType: PartnerStatus.WITHDRAWN,
                      partner: partner,
                    },
                  }),
                );
                setAnchorEl(null);
              }}
            >
              <>
                {t('more.withdraw.title')}
                {(isStatusDisabled || isWithdrawDisabled) && <InfoOutlinedIcon style={{ maxHeight: '15px' }} />}
              </>
            </MenuItem>
          </span>
        </Tooltip>

        <Tooltip title={tooltipFeedbackProspectReActivate} placement="left-start" arrow>
          <span id="prospect-reactivate-span">
            <MenuItem
              disabled={
                isReactivateStatusDisabled || isProspectReActivateDisabled || hasAnyInActiveBRWithLifecycleAsClosed
              }
              id="menuitem-prospect-reactivate"
              onClick={() => {
                dispatch(
                  openModal({
                    modalType: 'RejectWithdrawDialog',
                    modalProps: {
                      modalType: PartnerStatus.READY_FOR_ONBOARDING,
                      partner: partner,
                    },
                  }),
                );
                setAnchorEl(null);
              }}
            >
              {t('more.reActivate.title')}
              {(isReactivateStatusDisabled || isProspectReActivateDisabled) && (
                <InfoOutlinedIcon style={{ maxHeight: '15px' }} />
              )}
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem disabled={!partner?.imported} id="menuitem-crm" onClick={() => window.open(crmLink)}>
          View in CRM
        </MenuItem>

        {user?.hasGdprRequestAccess && (
          <MenuItem
            id="menuitem-gdpr-request"
            onClick={() => {
              dispatch(
                openModal({
                  modalType: 'GDPRModal',
                  modalProps: {
                    partnerType,
                    techId,
                  },
                }),
              );
              setAnchorEl(null);
            }}
          >
            {t('more.gdprRequest.title')}
          </MenuItem>
        )}

        {user?.hasGdprDeletionAccess && (
          <MenuItem
            id="menuitem-gdpr-deletion-request"
            onClick={() => {
              dispatch(
                openModal({
                  modalType: 'GdprDataRemovalModal',
                  modalProps: {
                    partnerType,
                    techId,
                  },
                }),
              );
              setAnchorEl(null);
            }}
          >
            {t('more.gdprRemoval.title')}
          </MenuItem>
        )}

        {user?.hasReassignRmAuthority && (
          <Tooltip title={tooltipFeedbackReassign} placement="left-start" id="reassign-tooltip" arrow>
            <span id="reject-span">
              <MenuItem
                id="rm-reassign-button"
                disabled={isStatusDisabledForReassign || partner?.imported || isDLMPartnerAndHasDraftVersion}
                onClick={() => {
                  dispatch(
                    openModal({
                      modalType: 'ReassignRmModal',
                      modalProps: { partner },
                    }),
                  );
                  setAnchorEl(null);
                }}
              >
                <>
                  {t('more.rm-reassign.title')}
                  {isStatusDisabledForReassign && <InfoOutlinedIcon style={{ maxHeight: '15px' }} />}
                </>
              </MenuItem>
            </span>
          </Tooltip>
        )}
      </Menu>
    </>
  );
};

export default RejectWithdrawButton;
